<template>
    <v-col class="pie-container">
        <!-- Static background pie -->
        <div class="pie no-round" style="--w:30vh; --p:100; --c:#a0a0a0;"></div>

        <!-- Animated pie with JS-driven percentage -->
        <div class="pie no-round" :style="pieStyle">
            <div class="pie-inside" :style="{ color: theme.textColor }">
                <!-- Label following the pie animation -->
                <div class="info-text-progress" :style="labelPosition">
                    {{ internalTotalValue }}
                </div>
                <div
                    class="info-text-remaining"
                    v-if="totalPiePercentage < 100"
                    :style="labelRemainingPosition"
                >
                    {{ maxValue - internalTotalValue }}
                </div>
                <div class="middle-div">{{ totalPiePercentage }} %</div>
            </div>
        </div>
    </v-col>
</template>
<script>
import {layoutMixin} from "../../utils/layout-mixin";

export default {
    mixins: [layoutMixin],
    props: {
        maxValue: {
            type: Number,
            default: 0,
            validator: Number.isInteger,
        },
        totalValue: {
            type: Number,
            default: 0,
            validator: Number.isInteger,
        },
    },
    data() {
        return {
            animatedPercentage: 0,
        };
    },
    computed: {
        internalTotalValue() {
            return this.maxValue === 0
                ? this.totalValue
                : Math.min(this.totalValue, this.maxValue);
        },
        totalPiePercentage() {
            return ((this.internalTotalValue * 100) / this.maxValue).toFixed(2);
        },
        pieStyle() {
            return {
                '--w': '30vh',
                '--p': this.animatedPercentage,
                '--c': '#06d00a',
            };
        },
        labelPosition() {
            // Midpoint of the filled area
            return this.calculateLabelPosition(this.animatedPercentage, false);
        },
        labelRemainingPosition() {
            // Midpoint of the remaining area
            const start = this.animatedPercentage;
            const remaining = 100 - start;
            const midOfRemainingArc = start + remaining / 2;
            return this.calculateLabelPosition(midOfRemainingArc, true);
        },
    },
    watch: {
        totalPiePercentage: {
            immediate: true,
            handler(newVal) {
                this.animatePercentage(parseFloat(newVal));
            },
        },
    },
    methods: {
        animatePercentage(target) {
            const duration = 1000;
            const start = performance.now();
            const initial = this.animatedPercentage;

            const animate = (now) => {
                const elapsed = now - start;
                const progress = Math.min(elapsed / duration, 1);
                this.animatedPercentage = initial + (target - initial) * progress;

                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        },
        calculateLabelPosition(p, remaining) {
            const w = 30 * window.innerHeight / 100;
            const radius = (w - 30 * 2) / 2;
            let angle = (p * 0.01 * Math.PI);
            if (remaining) {
                angle = (p / 100) * 2 * Math.PI;
            }
            const x = 50 + Math.sin(angle) * (radius / w) * 100;
            const y = 50 - Math.cos(angle) * (radius / w) * 100;
            return {
                top: `${y}%`,
                left: `${x}%`,
                transform: 'translate(-50%, -50%)',
                position: 'absolute',
                whiteSpace: 'nowrap',
                fontSize: '15px',
                color: '#000',
            };
        },
    },
};
</script>
<style scoped lang="scss">
.pie-container {
    max-height: 280px;
    position: relative;

    div.pie:nth-child(1),
    div.pie:nth-child(2) {
        position: absolute;
        left: calc(50% - 15vh);
        top: 10%;
    }
    .pie {
        --b: 7vh;
        width: var(--w);
        aspect-ratio: 1;
        position: relative;
        display: inline-grid;
        place-content: center;
        font-size: 25px;
        font-weight: bold;
        font-family: sans-serif;
    }
    .pie:before {
        content: '';
        position: absolute;
        border-radius: 50%;
        inset: 0;
        background:
            radial-gradient(farthest-side, var(--c) 98%, #0000) top/var(--b) var(--b) no-repeat,
            conic-gradient(var(--c) calc(var(--p) * 1%), #0000 0);
        -webkit-mask: radial-gradient(farthest-side, #0000 calc(99% - var(--b)), #000 calc(100% - var(--b)));
        mask: radial-gradient(farthest-side, #0000 calc(99% - var(--b)), #000 calc(100% - var(--b)));
    }
    .pie-inside {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
    .middle-div {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
    }
    .no-round:before {
        background-size: 0 0, auto;
    }
    .no-round:after {
        content: none;
    }
}
</style>
